import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { money, relative } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Badge, Busy, Button, Card, EmptyState, ErrorState } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, Textarea } from '@/components/ui/form'
import { SortableTh, Table, Td, Th } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { ApprovalInboxItem, Paged } from '@/types/api'

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * Oldest first: the point of an approval inbox is what has been waiting longest.
 */
const DEFAULT_SORT = 'waiting'

export function Approvals() {
  const { can } = useAuth()
  const { current } = useCountry()
  const navigate = useNavigate()
  const [showEverything, setShowEverything] = useState(false)
  const [search, setSearch] = useState('')
  const [minimumAge, setMinimumAge] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [deciding, setDeciding] = useState<{ item: ApprovalInboxItem; approve: boolean } | undefined>()

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  const inbox = useAsync<Paged<ApprovalInboxItem>>(
    (signal) =>
      api.get<Paged<ApprovalInboxItem>>(
        '/approvals',
        {
          countryId: current?.countryId,
          all: showEverything,
          search,
          waitingAtLeastDays: minimumAge || undefined,
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    [current?.countryId, showEverything, search, minimumAge, sort, page, pageSize],
  )

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Approvals"
        hint="What is waiting on you. Your own quotations appear here only if you may approve them yourself - which, apart from the Owner, nobody may."
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'waiting', label: 'Longest waiting' },
          { value: '-waiting', label: 'Most recent' },
          { value: '-value', label: 'Highest value' },
          { value: 'customer', label: 'Customer A–Z' },
          { value: 'quotation', label: 'Quotation number' },
        ]}
      >
        {/* Only for somebody who can act on any step. The checkbox this replaces said the
            same thing and floated above the table on its own. */}
        {can('quotation.approve.any') && (
          <FilterSelect
            label="Waiting on"
            width="w-56"
            value={showEverything ? 'all' : 'mine'}
            onChange={(next) => refine(() => setShowEverything(next === 'all'))}
          >
            <option value="mine">My steps</option>
            <option value="all">Everything in my countries</option>
          </FilterSelect>
        )}

        <FilterSelect
          label="Age"
          width="w-40"
          value={minimumAge}
          onChange={(next) => refine(() => setMinimumAge(next))}
        >
          <option value="">Any</option>
          <option value="3">3 days or more</option>
          <option value="7">7 days or more</option>
        </FilterSelect>

        <div className="w-64">
          <Input
            placeholder="Search number, customer or preparer"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>
      </ListToolbar>

      {inbox.error && <ErrorState title={inbox.error.title} detail={inbox.error.detail} onRetry={inbox.reload} />}

      <Busy busy={inbox.loading}>
        {inbox.data && (
          <Card>
            {inbox.data.items.length === 0 ? (
              <EmptyState
                title="Nothing waiting on you"
                hint="Approval requests land here the moment a quotation is submitted, and you are told by email as well."
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="quotation" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Quotation
                    </SortableTh>
                    <Th>Why you</Th>
                    <SortableTh field="value" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))} numeric>
                      Value
                    </SortableTh>
                    <SortableTh field="waiting" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Waiting
                    </SortableTh>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {inbox.data.items.map((item) => (
                    <tr key={item.id}>
                      <Td>
                        <button
                          type="button"
                          className="text-left"
                          onClick={() => navigate(`/quotations/${item.quotationId}`)}
                        >
                          <span className="tabular text-[13px]">{item.quotationNo}</span>
                          <span className="block font-medium">{item.customerName}</span>
                          <span className="block text-[12px] text-muted">
                            {item.title ?? 'No title'} · by {item.preparedBy}
                          </span>
                        </button>
                      </Td>
                      <Td>
                        <span className="text-[13px]">{item.currentStepLabel}</span>
                        <span className="block text-[12px] text-muted">
                          {item.triggers.map((trigger) => trigger.explanation).join(' · ')}
                        </span>
                        {/* An Owner acting on somebody else's step is an override: it is
                            recorded and flagged, and it never skips a step. */}
                        {item.wouldBeAnOverride && <Badge tone="warning">Would be an override</Badge>}
                      </Td>
                      <Td numeric>{money(item.grandTotal, item.currencyCode)}</Td>
                      <Td className="text-muted">
                        {/* One reading of the same fact, not two. Past three days it turns
                            amber, because that is the point at which it needs chasing. */}
                        {item.waitingDays >= 3 ? (
                          <span className="text-warn">{item.waitingDays} days</span>
                        ) : (
                          relative(item.submittedAt)
                        )}
                      </Td>
                      <Td>
                        <span className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => setDeciding({ item, approve: false })}>
                            Send back
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => setDeciding({ item, approve: true })}
                          >
                            Approve
                          </Button>
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={inbox.data.page}
              pageSize={inbox.data.pageSize}
              totalCount={inbox.data.totalCount}
              totalPages={inbox.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <Decision
        decision={deciding}
        onClose={() => setDeciding(undefined)}
        onDone={() => {
          setDeciding(undefined)
          inbox.reload()
        }}
      />
    </>
  )
}

function Decision({
  decision,
  onClose,
  onDone,
}: {
  decision: { item: ApprovalInboxItem; approve: boolean } | undefined
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)

  const approving = decision?.approve ?? false

  async function decide() {
    if (!decision) return

    setSaving(true)
    try {
      const result = await api.post<{ nextStepLabel: string | null; quotationStatus: string }>(
        `/approvals/${decision.item.id}/${approving ? 'approve' : 'reject'}`,
        { comment: comment || null },
      )

      toast.show(
        approving
          ? result.nextStepLabel
            ? `Approved. Now with ${result.nextStepLabel}.`
            : 'Approved. The chain is complete.'
          : 'Sent back to the preparer with your reason.',
        'success',
      )

      setComment('')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not record the decision', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={decision !== undefined}
      title={approving ? 'Approve this quotation' : 'Send it back'}
      description={
        decision
          ? `${decision.item.quotationNo} · ${decision.item.customerName} · ${money(decision.item.grandTotal, decision.item.currencyCode)}`
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={approving ? 'primary' : 'danger'}
            loading={saving}
            disabled={!approving && comment.trim().length === 0}
            onClick={() => void decide()}
          >
            {approving ? 'Approve' : 'Send back'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {decision?.item.wouldBeAnOverride && (
          <p className="rounded-md bg-warn-wash px-3 py-2 text-[13px] text-warn">
            This step names somebody else. You can act on it, and it will be recorded as an
            override against your name - it does not skip anybody.
          </p>
        )}

        <Field
          label={approving ? 'Comment' : 'What needs changing'}
          required={!approving}
          hint={
            approving
              ? 'Optional, and kept with the approval record.'
              : 'Required. This is the whole message the rep gets - without it they are guessing.'
          }
        >
          {(id) => <Textarea id={id} value={comment} onChange={(event) => setComment(event.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}
